import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

export type InterfaceMode = "professional" | "guided";

type InterfaceModeState = {
  mode: InterfaceMode;
  guided: boolean;
  setMode: (mode: InterfaceMode) => void;
};

const STORAGE_KEY = "whaleseek_interface_mode";
const InterfaceModeContext = createContext<InterfaceModeState | null>(null);

function storedMode(): InterfaceMode | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "professional" || value === "guided" ? value : null;
  } catch {
    return null;
  }
}

export function InterfaceModeProvider({ children }: { children: ReactNode }) {
  const { me, refresh } = useAuth();
  const [mode, setModeState] = useState<InterfaceMode>(() => storedMode() ?? "professional");

  useEffect(() => {
    const local = storedMode();
    if (local) {
      setModeState(local);
      return;
    }
    const preferred = me?.preferences.interface_mode;
    if (preferred === "professional" || preferred === "guided") {
      setModeState(preferred);
    } else if (me) {
      setModeState(me.preferences.onboarding_completed === true ? "professional" : "guided");
    }
  }, [me?.id, me?.preferences.interface_mode, me?.preferences.onboarding_completed]);

  const setMode = (next: InterfaceMode) => {
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* local persistence is optional */ }
    if (me) void api.authPatchPreferences({ interface_mode: next }).then(() => refresh()).catch(() => undefined);
  };

  return <InterfaceModeContext.Provider value={{ mode, guided: mode === "guided", setMode }}>
    {children}
  </InterfaceModeContext.Provider>;
}

export function useInterfaceMode() {
  const context = useContext(InterfaceModeContext);
  if (!context) throw new Error("useInterfaceMode must be used within InterfaceModeProvider");
  return context;
}
